/**
 * The bridge's per-install device key (architecture §B "Device key", W11-BRIDGEKEY).
 *
 * Same key, same proofs as the MAUI app:
 *   - alias cb_device_v1, EC P-256, generated INSIDE the Android Keystore
 *     (StrongBox when the device has one, the TEE otherwise), non-exportable;
 *     the native half is DeviceKeyPlugin.java (frontend/android/.../devicekey/)
 *     and generates the key with exactly the parameters of
 *     native/CommandBridge.Field/Platforms/Android/AndroidKeystoreDeviceKeyStore.cs;
 *   - public key sent as base64 SPKI DER, key id = sha256 hex of that DER;
 *   - signatures SHA256withECDSA, DER encoded (RFC 3279);
 *   - proof messages are the exact bytes of backend device-key.ts
 *     buildDeviceKeyProofMessage / buildDeviceRefreshProofMessage and of
 *     native DeviceKeyProofs: UTF-8 lines joined by "\n", no trailing newline.
 *
 * Why the SAME Keystore entry: the bridge and the production MAUI app share the
 * application id (com.cmdbridge.app.us) and the app signing key, so the MAUI
 * build installs as an in-place update of the bridge and keeps its Linux UID.
 * Android Keystore entries are scoped to that UID, so after the update the MAUI
 * AndroidKeystoreDeviceKeyStore finds cb_device_v1 with ContainsAlias and uses
 * it (it never replaces an existing key). A different package (the MAUI preview
 * channel, com.cmdbridge.app.us.preview) or a different signing key cannot
 * see it, by design.
 *
 * Only the Android shell has the plugin. The web console and iOS (not shipped)
 * get `null` from getNativeDeviceKeySigner() and register unbound, as before.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';

export const DEVICE_KEY_ALIAS = 'cb_device_v1';
export const DEVICE_KEY_ALGORITHM = 'ECDSA_P256';
export const REGISTER_PROOF_PREFIX = 'cmdbridge-device-register:v1';
export const REFRESH_PROOF_PREFIX = 'cmdbridge-device-refresh:v1';
/**
 * The only proofs the native DeviceKey plugin signs (ProofMessagePolicy.java,
 * wave-11 residual 64). The plugin takes no bytes to sign: it receives each
 * proof's FIELDS, validates them (UUIDs, sha256 hex, the server's nonce shape,
 * a signed_at within 5 minutes of the device clock) and builds the message
 * natively, with its OWN key id. A new proof kind needs a new native method.
 */
export const SIGNABLE_PROOF_PREFIXES = [REGISTER_PROOF_PREFIX, REFRESH_PROOF_PREFIX] as const;

/** The registration proof's fields (backend buildDeviceKeyProofMessage). */
export interface RegistrationProofFields {
  userId: string;
  tenantId: string;
  /** The id of the key that signs (the caller derives it from publicKey/getOrCreatePublicKey). */
  keyId: string;
  signedAt: string;
}

/** The refresh proof's fields (backend buildDeviceRefreshProofMessage); the credential goes in as its sha256 hex. */
export interface RefreshProofFields {
  deviceSessionId: string;
  keyId: string;
  refreshTokenHash: string;
  nonce: string;
  signedAt: string;
}

/** What signs for this install. Every method may reject (Keystore busy, no key, a field refused). */
export interface DeviceKeySigner {
  /** The SPKI DER public key, generating the key pair when there is none. */
  getOrCreatePublicKey(): Promise<Uint8Array>;
  /** The existing key's SPKI DER, or null when the install has no key. Never generates. */
  publicKey(): Promise<Uint8Array | null>;
  /** SHA256withECDSA (DER) over registrationProofMessage(fields). Never generates a key. */
  signRegistrationProof(fields: RegistrationProofFields): Promise<Uint8Array>;
  /** SHA256withECDSA (DER) over refreshProofMessageFromHash(fields). Never generates a key. */
  signRefreshProof(fields: RefreshProofFields): Promise<Uint8Array>;
}

/** The registration body's `device_key` (backend deviceKeySchema). */
export interface DeviceKeyRegistration {
  alias: typeof DEVICE_KEY_ALIAS;
  algorithm: typeof DEVICE_KEY_ALGORITHM;
  public_key: string;
  proof: { signed_at: string; signature: string };
}

export interface DeviceKeyProof {
  signed_at: string;
  signature: string;
}

// ---------------------------------------------------------------------------
// Bytes
// ---------------------------------------------------------------------------

const utf8 = new TextEncoder();

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

function hex(bytes: ArrayBuffer): string {
  return Array.from(new Uint8Array(bytes), (b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(data: Uint8Array): Promise<string> {
  // A typed-array copy (not its .buffer): an ArrayBuffer from another realm is refused by some engines.
  return hex(await crypto.subtle.digest('SHA-256', new Uint8Array(data)));
}

/** The key id the server derives: lower-case sha256 hex of the SPKI DER. */
export function deviceKeyId(spki: Uint8Array): Promise<string> {
  return sha256Hex(spki);
}

/** ISO 8601 UTC with milliseconds, the format native DeviceKeyProofs.FormatSignedAt emits. */
export function formatSignedAt(ms: number): string {
  return new Date(ms).toISOString();
}

function joinLines(lines: string[]): Uint8Array {
  for (const line of lines) {
    if (typeof line !== 'string' || line.length === 0 || line.includes('\n')) {
      throw new Error('A device-key proof field is missing or contains a newline.');
    }
  }
  return utf8.encode(lines.join('\n'));
}

/** backend buildDeviceKeyProofMessage. */
export function registrationProofMessage(p: { userId: string; tenantId: string; keyId: string; signedAt: string }): Uint8Array {
  return joinLines([REGISTER_PROOF_PREFIX, p.userId, p.tenantId, DEVICE_KEY_ALIAS, p.keyId, p.signedAt]);
}

/** sha256 hex of the refresh credential: what the refresh proof carries (never the credential itself). */
export function refreshTokenHash(refreshToken: string): Promise<string> {
  return sha256Hex(utf8.encode(refreshToken));
}

/** backend buildDeviceRefreshProofMessage, from the credential's sha256 hex. */
export function refreshProofMessageFromHash(p: RefreshProofFields): Uint8Array {
  return joinLines([REFRESH_PROOF_PREFIX, p.deviceSessionId, p.keyId, p.refreshTokenHash, p.nonce, p.signedAt]);
}

/** backend buildDeviceRefreshProofMessage; the credential goes in as its sha256 hex. */
export async function refreshProofMessage(p: {
  deviceSessionId: string;
  keyId: string;
  refreshToken: string;
  nonce: string;
  signedAt: string;
}): Promise<Uint8Array> {
  return refreshProofMessageFromHash({
    deviceSessionId: p.deviceSessionId,
    keyId: p.keyId,
    refreshTokenHash: await refreshTokenHash(p.refreshToken),
    nonce: p.nonce,
    signedAt: p.signedAt,
  });
}

// ---------------------------------------------------------------------------
// Signing
// ---------------------------------------------------------------------------

/**
 * The registration `device_key`, or null when this install cannot produce one
 * right now (no plugin, no hardware key storage, Keystore busy): registration
 * then goes ahead unbound, exactly as the native TryBindDeviceKeyAsync does.
 */
export async function signRegistration(
  signer: DeviceKeySigner | null,
  p: { userId: string; tenantId: string; now: number }
): Promise<{ deviceKey: DeviceKeyRegistration; keyId: string } | null> {
  if (!signer) return null;
  try {
    const spki = await signer.getOrCreatePublicKey();
    const keyId = await deviceKeyId(spki);
    const signedAt = formatSignedAt(p.now);
    const signature = await signer.signRegistrationProof({ userId: p.userId, tenantId: p.tenantId, keyId, signedAt });
    return {
      keyId,
      deviceKey: {
        alias: DEVICE_KEY_ALIAS,
        algorithm: DEVICE_KEY_ALGORITHM,
        public_key: bytesToBase64(spki),
        proof: { signed_at: signedAt, signature: bytesToBase64(signature) },
      },
    };
  } catch {
    return null;
  }
}

/**
 * The refresh `device_key_proof` for a key-bound session, or null when the key
 * cannot sign right now. Like native SignRefreshProofAsync, a key whose id
 * differs from the bound one still signs: the server's (non-terminal) refusal
 * decides, never a guess made on the device.
 */
export async function signRefresh(
  signer: DeviceKeySigner | null,
  p: { deviceSessionId: string; refreshToken: string; nonce: string; now: number }
): Promise<{ proof: DeviceKeyProof; keyId: string } | null> {
  if (!signer) return null;
  try {
    const spki = await signer.publicKey();
    if (!spki) return null;
    const keyId = await deviceKeyId(spki);
    const signedAt = formatSignedAt(p.now);
    const signature = await signer.signRefreshProof({
      deviceSessionId: p.deviceSessionId,
      keyId,
      refreshTokenHash: await refreshTokenHash(p.refreshToken),
      nonce: p.nonce,
      signedAt,
    });
    return { keyId, proof: { signed_at: signedAt, signature: bytesToBase64(signature) } };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// The Android plugin
// ---------------------------------------------------------------------------

/** The native plugin (DeviceKeyPlugin.java). There is no method that signs caller bytes. */
export interface DeviceKeyPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  getOrCreate(): Promise<{ publicKey: string; algorithm: string; alias: string; residence: string }>;
  publicKey(): Promise<{ publicKey: string | null }>;
  signRegistrationProof(options: { userId: string; tenantId: string; signedAt: string }): Promise<{ signature: string; keyId: string }>;
  signRefreshProof(options: {
    deviceSessionId: string;
    refreshTokenHash: string;
    nonce: string;
    signedAt: string;
  }): Promise<{ signature: string; keyId: string }>;
}

let devicePlugin: DeviceKeyPlugin | null = null;

/** Registered on first use, so importing this module loads nothing native. */
function deviceKeyPlugin(): DeviceKeyPlugin {
  if (!devicePlugin) devicePlugin = registerPlugin<DeviceKeyPlugin>('DeviceKey');
  return devicePlugin;
}

export function createPluginSigner(plugin: DeviceKeyPlugin = deviceKeyPlugin()): DeviceKeySigner {
  return {
    async getOrCreatePublicKey() {
      const { publicKey, algorithm, alias } = await plugin.getOrCreate();
      if (algorithm !== DEVICE_KEY_ALGORITHM || alias !== DEVICE_KEY_ALIAS) {
        throw new Error('The device key plugin answered for another key.');
      }
      return base64ToBytes(publicKey);
    },
    async publicKey() {
      const { publicKey } = await plugin.publicKey();
      return typeof publicKey === 'string' && publicKey.length > 0 ? base64ToBytes(publicKey) : null;
    },
    async signRegistrationProof(fields) {
      const { signature, keyId } = await plugin.signRegistrationProof({
        userId: fields.userId,
        tenantId: fields.tenantId,
        signedAt: fields.signedAt,
      });
      // The plugin signs with ITS key id; a different one means the key changed in between.
      if (keyId !== fields.keyId) throw new Error('The device key changed while signing.');
      return base64ToBytes(signature);
    },
    async signRefreshProof(fields) {
      const { signature, keyId } = await plugin.signRefreshProof({
        deviceSessionId: fields.deviceSessionId,
        refreshTokenHash: fields.refreshTokenHash,
        nonce: fields.nonce,
        signedAt: fields.signedAt,
      });
      if (keyId !== fields.keyId) throw new Error('The device key changed while signing.');
      return base64ToBytes(signature);
    },
  };
}

/** The Android shell's signer, or null anywhere else (web, iOS, a shell without the plugin). */
export function getNativeDeviceKeySigner(): DeviceKeySigner | null {
  try {
    if (Capacitor.getPlatform() !== 'android' || !Capacitor.isPluginAvailable('DeviceKey')) return null;
  } catch {
    return null;
  }
  return createPluginSigner();
}
